import React, { useEffect, useState } from "react";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import CrisisAlertIcon from "@mui/icons-material/CrisisAlert";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import AttractionsIcon from "@mui/icons-material/Attractions";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import MilitaryTechIcon from "@mui/icons-material/MilitaryTech";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/auth.context.js";
import {
  getTeamsJoinedByCaptain,
  join,
  leave,
} from "../../api/tournament.api.js";
import { Box, Chip, Divider, Stack } from "@mui/material";
import WinnerCard from "./winner.card.js";
import StatusChip from "../chips/status.chip.js";
import ChooseTeamModal from "../modals/chooseTeam.modal.js";
import { useSnackbar } from "../../context/snackbar.context.js";
import FestivalIcon from "@mui/icons-material/Festival";

const TournamentCard = ({ tournament, refresh }) => {
  const [openModal, setOpenModal] = useState(false);
  const [joinedTeamsByUser, setJoinedTeamsByUser] = useState([]);
  const navigate = useNavigate();
  const auth = useAuth();
  const showSnackbar = useSnackbar();

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleNavigateDetails = () => {
    navigate("/tournament", { state: { tournament } });
  };

  useEffect(() => {
    if (
      tournament.status === "Open" &&
      tournament.participantMode === "CustomTeams"
    ) {
      getTeamsJoinedByCaptain(tournament.id, auth.user.id)
        .then((response) => setJoinedTeamsByUser(response.data))
        .catch((error) => console.log(error));
    }
  }, [tournament]);

  const isJoinable = () => {
    return (
      (tournament.status === "Open" &&
        tournament.participantMode !== "CustomTeams" &&
        !tournament.participantIds.find((id) => id === auth.user.id)) ||
      (tournament.status === "Open" &&
        tournament.participantMode === "CustomTeams" &&
        joinedTeamsByUser.length === 0)
    );
  };

  const isLeavable = () => {
    return (
      tournament.status === "Open" &&
      (tournament.participantIds.find((id) => id === auth.user.id) ||
        joinedTeamsByUser.length > 0)
    );
  };

  const handleJoin = () => {
    if (
      tournament.status === "Open" &&
      tournament.participantMode !== "CustomTeams"
    ) {
      join(tournament.id, auth.user.id)
        .then((result) => {
          if (!result.data) {
            showSnackbar("Could not join!", "error");
          } else {
            showSnackbar("Joined successfully!", "success");
          }
        })
        .catch((error) => {
          showSnackbar("Could not join! Unknown Error", "error");
        });
    } else {
      handleOpenModal();
    }
  };

  const handleLeave = () => {
    leave(tournament.id, auth.user.id)
      .then((response) => {
        if (response.data) {
          showSnackbar("Leaved successfully!", "success");
        } else {
          showSnackbar("Leaving failed!", "error");
        }
      })
      .catch(() => showSnackbar("Leaving failed!", "error"));
  };

  return (
    <Card
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        minWidth: 300,
        maxWidth: 300,
        minHeight: 350,
        maxHeight: 350,
        filter: tournament.status === "Created" ? "blur(5px)" : "",
        backgroundColor:
          tournament.status === "Created" ? "rgba(0, 0, 0, 0.5)" : "",
        overflow: "hidden", // wichtig für das ::before
        "&::before": tournament.game.image
          ? {
              content: '""',
              position: "absolute",
              inset: 0,
              backgroundImage: `linear-gradient(
              rgba(0,0,0,0.5), 
              rgba(18,18,18,1)
            ), url(/static/images/${tournament.game.image})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              opacity: 0.4,
              transition: "opacity 0.4s ease",
              zIndex: 0,
            }
          : {},
        "&:hover::before": {
          transition: "opacity 0.4s ease",
          opacity: 0, // Hover-Effekt
        },
        "& > *": {
          position: "relative",
          zIndex: 1, // sorgt dafür, dass der Inhalt über dem Hintergrundbild bleibt
        },
      }}
      className={`card ${tournament.status.toLowerCase()}`}
    >
      <CardContent>
        <Box>
          <Box>
            <Box style={{ alignItems: "center", justifyContent: "center", display:"flex" }}>
              <FestivalIcon color="primary" sx={{ fontSize: 24, mr: 1 }}/>
              <Typography variant="h6" gutterBottom color="primary">
                {tournament.name}
              </Typography>
              {tournament.isMainTournament && (
                <MilitaryTechIcon
                  color="primary"
                  sx={{ fontSize: 24, ml: 1 }}
                />
              )}
            </Box>

            <Divider variant="middle" sx={{ mt: 1, mb: 1 }} />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box style={{ alignItems: "center", justifyContent: "center", display:"flex" }}>
                <Typography sx={{ color: "text.secondary", mr: 3 }}>
                  {tournament.game.name}
                </Typography>
                {tournament.game.category === "Shooter" && (
                  <CrisisAlertIcon sx={{ fontSize: 16, mr: 1 }} />
                )}
                {tournament.game.category === "Racing" && (
                  <DirectionsCarIcon sx={{ fontSize: 16, mr: 1 }} />
                )}
                {tournament.game.category === "Strategy" && (
                  <AccountBalanceIcon sx={{ fontSize: 16, mr: 1 }} />
                )}
                {tournament.game.category === "Other" && (
                  <AttractionsIcon sx={{ fontSize: 16, mr: 1 }} />
                )}
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {tournament.game.category}
                </Typography>
              </Box>

              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <StatusChip status={tournament.status} />

                <Chip
                  label={tournament.participantMode}
                  variant="outlined"
                  size="small"
                />

                <Chip
                  label={tournament.system}
                  variant="outlined"
                  size="small"
                />
              </Stack>
            </Box>
            <Divider variant="middle" sx={{ mt: 1, mb: 1 }} />
          </Box>
          {!tournament.winner && (
            <Box>
              <Typography
                variant="body"
                sx={{
                  color: "text.secondary",
                  fontSize: 10,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "250px",
                  maxHeight: "100px",
                  WebkitLineClamp: 4,
                  WebkitBoxOrient: "vertical",
                  display: "-webkit-box",
                }}
              >
                {tournament.description}
              </Typography>

              <Typography sx={{ color: "text.secondary", fontSize: 12, mt: 2 }}>
                Start: {moment(tournament.start).format("DD.MM.YYYY HH:mm")}
              </Typography>
              <Typography sx={{ color: "text.secondary", fontSize: 12 }}>
                Participants: {tournament.participantIds.length} /{" "}
                {tournament.participantCountMax}
              </Typography>
              {tournament.participantMode === "CustomTeams" && (
                <Typography sx={{ color: "text.secondary", fontSize: 12 }}>
                  Teamsize: {tournament.teamSize}
                </Typography>
              )}
              {tournament.prize && (
                <Typography sx={{ color: "text.secondary", fontSize: 12 }}>
                  Prize: {tournament.prize}
                </Typography>
              )}
            </Box>
          )}
          <Box style={{ alignItems: "center", justifyContent: "center", display:"flex" }}>
            {tournament.winner && <WinnerCard tournament={tournament} />}
          </Box>
        </Box>
      </CardContent>
      <CardActions>
        {isJoinable() && (
          <Button onClick={handleJoin} size="small">
            Join!
          </Button>
        )}
        {isLeavable() && (
          <Button onClick={handleLeave} size="small">
            Leave
          </Button>
        )}
        {tournament.status !== "Created" && (
          <Button size="small" onClick={handleNavigateDetails}>
            Details
          </Button>
        )}
      </CardActions>

      {tournament.status === "Open" &&
        tournament.participantMode === "CustomTeams" && (
          <ChooseTeamModal
            aria-modal
            open={openModal}
            handleClose={handleCloseModal}
            tournament={tournament}
            refresh={refresh}
          />
        )}
    </Card>
  );
};

export default TournamentCard;
